import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  Box, Typography, Button, Card, Stack, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  CircularProgress, Autocomplete,
} from '@mui/material'
import { Add, Delete, Campaign } from '@mui/icons-material'
import { PageHeader } from '../../components/PageHeader'
import { api } from '../../lib/api'
import { unwrapList } from '../../lib/unwrap'
import {
  useAudiences, useCreateAudience, useDeleteAudience,
  useAudienceMembers, useAddAudienceMember, useRemoveAudienceMember,
} from '../../hooks/useAudiences'
import { usePermissions, PERMS } from '../../hooks/usePermissions'

export default function AudienceGroupsPage() {
  const { t } = useTranslation()
  const { hasPermission } = usePermissions()
  const canManage = hasPermission(PERMS.MANAGE_NOTIFICATION_ROUTING)

  const { data: groups = [], isLoading } = useAudiences()
  const createGroup = useCreateAudience()
  const deleteGroup = useDeleteAudience()

  const [createOpen, setCreateOpen] = useState(false)
  const [form, setForm] = useState<{ name: string; description: string; preset: 'generic' | 'school' }>({
    name: '',
    description: '',
    preset: 'generic',
  })
  const [manageGroup, setManageGroup] = useState<string | null>(null)

  const handleCloseCreate = () => {
    setCreateOpen(false)
    setForm({ name: '', description: '', preset: 'generic' })
  }

  const handleCreate = async () => {
    try {
      await createGroup.mutateAsync({
        name: form.name,
        description: form.description || undefined,
        preset: form.preset,
      })
      handleCloseCreate()
    } catch {
      // Error toast shown by API interceptor
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress sx={{ color: '#34A8E0' }} />
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader
        title={t('audiences.title')}
        subtitle={t('audiences.subtitle')}
        action={
          canManage ? (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreateOpen(true)}
              sx={{ background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)' }}
            >
              {t('audiences.create')}
            </Button>
          ) : undefined
        }
      />

      <Stack spacing={2}>
        {groups.length === 0 && (
          <Card sx={{ p: 6, textAlign: 'center', border: '1px solid #e2e8f0' }}>
            <Campaign sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
            <Typography variant="body2" color="#64748b">{t('audiences.noGroups')}</Typography>
          </Card>
        )}
        {groups.map((g) => (
          <Card key={g.id} sx={{ p: 2, border: '1px solid #e2e8f0' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Campaign sx={{ color: '#34A8E0' }} />
                <Box>
                  <Typography variant="body1" fontWeight={700}>{g.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {g.description || t('deviceGroups.noDescription')}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip
                  label={g.preset === 'school' ? t('audiences.presetSchool') : t('audiences.presetGeneric')}
                  size="small"
                />
                <Button size="small" onClick={() => setManageGroup(g.id)}>{t('audiences.members')}</Button>
                {canManage && (
                  <Tooltip title={t('common.delete')}>
                    <IconButton size="small" color="error" onClick={() => { deleteGroup.mutate(g.id) }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Card>
        ))}
      </Stack>

      {/* Create Dialog */}
      <Dialog open={createOpen} onClose={handleCloseCreate} maxWidth="xs" fullWidth>
        <DialogTitle>{t('audiences.create')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label={t('common.name')}
            required
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          />
          <TextField
            label={t('common.description')}
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
          />
          <TextField
            select
            label={t('audiences.preset')}
            value={form.preset}
            onChange={(e) => setForm((p) => ({ ...p, preset: e.target.value as 'generic' | 'school' }))}
          >
            <MenuItem value="generic">{t('audiences.presetGeneric')}</MenuItem>
            <MenuItem value="school">{t('audiences.presetSchool')}</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreate}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!form.name || createGroup.isPending}
            sx={{ background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)' }}
          >
            {t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Members Dialog */}
      {manageGroup && (
        <ManageAudienceDialog groupId={manageGroup} onClose={() => setManageGroup(null)} />
      )}
    </Box>
  )
}

function ManageAudienceDialog({ groupId, onClose }: { groupId: string; onClose: () => void }) {
  const { t } = useTranslation()
  const { hasPermission } = usePermissions()
  const canManage = hasPermission(PERMS.MANAGE_NOTIFICATION_ROUTING)

  const { data: members = [] } = useAudienceMembers(groupId)
  const addMember = useAddAudienceMember(groupId)
  const removeMember = useRemoveAudienceMember(groupId)

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get('/users').then((r) => unwrapList<{ id: string; email: string; name?: string }>(r.data)),
  })

  const [mode, setMode] = useState<'platform' | 'external'>('platform')
  const [selectedUser, setSelectedUser] = useState<{ id: string; email: string; name?: string } | null>(null)
  const [externalName, setExternalName] = useState('')
  const [externalPhone, setExternalPhone] = useState('')

  const handleAddPlatformUser = () => {
    if (!selectedUser) return
    addMember.mutate({ user_id: selectedUser.id, display_name: selectedUser.name ?? selectedUser.email })
    setSelectedUser(null)
  }

  const handleAddExternal = () => {
    if (!externalName || !externalPhone) return
    addMember.mutate({ display_name: externalName, phone: externalPhone })
    setExternalName('')
    setExternalPhone('')
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('audiences.members')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {members.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {members.map((m) => (
              <Chip
                key={m.id}
                label={m.display_name}
                onDelete={canManage ? () => removeMember.mutate(m.id) : undefined}
                variant="outlined"
                size="small"
              />
            ))}
          </Stack>
        )}

        {canManage && (
          <>
            <Stack direction="row" spacing={0.5}>
              <Button
                size="small"
                variant={mode === 'platform' ? 'contained' : 'outlined'}
                onClick={() => setMode('platform')}
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  ...(mode === 'platform'
                    ? { background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)' }
                    : { borderColor: '#e2e8f0', color: '#64748b' }),
                }}
              >
                {t('audiences.platformUser')}
              </Button>
              <Button
                size="small"
                variant={mode === 'external' ? 'contained' : 'outlined'}
                onClick={() => setMode('external')}
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  ...(mode === 'external'
                    ? { background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)' }
                    : { borderColor: '#e2e8f0', color: '#64748b' }),
                }}
              >
                {t('audiences.externalContact')}
              </Button>
            </Stack>

            {mode === 'platform' ? (
              <Stack direction="row" spacing={1}>
                <Autocomplete
                  size="small"
                  fullWidth
                  options={users}
                  getOptionLabel={(u) => u.name ?? u.email}
                  value={selectedUser}
                  onChange={(_, v) => setSelectedUser(v)}
                  renderInput={(params) => <TextField {...params} label={t('audiences.memberName')} />}
                />
                <Button
                  variant="contained"
                  onClick={handleAddPlatformUser}
                  disabled={!selectedUser || addMember.isPending}
                  sx={{ background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)', whiteSpace: 'nowrap' }}
                >
                  {t('audiences.addMember')}
                </Button>
              </Stack>
            ) : (
              <Stack spacing={1}>
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    fullWidth
                    label={t('audiences.memberName')}
                    value={externalName}
                    onChange={(e) => setExternalName(e.target.value)}
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label={t('audiences.memberPhone')}
                    value={externalPhone}
                    onChange={(e) => setExternalPhone(e.target.value)}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddExternal}
                    disabled={!externalName || !externalPhone || addMember.isPending}
                    sx={{ background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)', whiteSpace: 'nowrap' }}
                  >
                    {t('audiences.addMember')}
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {t('audiences.externalHint')}
                </Typography>
              </Stack>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  )
}
